import _ from 'lodash'

function convertJsonToCsv(jsonArray: Record<string, any>[], headers: string[]) {
  if (!jsonArray || jsonArray.length === 0) {
    return '';
  }
  
  // Create the CSV header row
  const csvHeader = headers.join(',');
  
  // Create the CSV data rows with optimized processing
  const csvRows = jsonArray.map(obj => {
    return headers
      .map(header => {
        const value = obj[header];
        
        // Handle null/undefined values
        if (value === null || value === undefined) {
          return '';
        }
        
        // Special handling for the Time column to ensure proper Excel formatting
        if (header === 'Time' && value) {
          return `"${value}"`;
        }
        
        // If the value contains commas, quotes, or newlines, wrap it in double quotes
        // and escape any existing quotes
        if (typeof value === 'string' && 
            (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes(' '))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        
        return value;
      })
      .join(',');
  });
  
  // Combine the header and rows to form the complete CSV string
  return [csvHeader, ...csvRows].join('\n');
}

interface DownloadFileParams {
  data: string;
  fileName: string;
  fileType: string;
}

/**
 * Creates and triggers download of a file
 */
const downloadFile = ({ data, fileName, fileType }: DownloadFileParams) => {
  // Create blob with the data
  const blob = new Blob([data], { type: fileType });
  
  // Create download link
  const a = document.createElement('a');
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  
  // Trigger download and clean up
  const clickEvent = new MouseEvent('click', {
    view: window,
    bubbles: true,
    cancelable: true,
  });
  
  document.body.appendChild(a); // Needed for Firefox
  a.dispatchEvent(clickEvent); // More reliable than a.click()
  document.body.removeChild(a);
  
  // Give browser a moment to process before revoking URL
  setTimeout(() => {
    window.URL.revokeObjectURL(a.href); // Free memory
  }, 100);
}

/**
 * Interface for the API response
 */
interface ApiResponse {
  success: boolean;
  code: number;
  message: string;
  data: {
    titles: string[];
    deals: any[];
  };
}

/**
 * Transforms API data for Deals CSV download with better performance
 * @param apiResponse Direct API response data
 * @param columns Array of column headers to include
 * @param fileName Base name for the downloaded file
 */
const downloadDealsToCSV = (apiResponse: ApiResponse, columns: string[], fileName: string) => {
  // Early return if no data
  if (!apiResponse?.data?.deals || apiResponse.data.deals.length === 0) {
    console.warn("No data available for download");
    return;
  }
  
  // Process in batches to prevent UI freeze
  const batchSize = 500;
  const deals = apiResponse.data.deals;
  const totalDeals = deals.length;
  let processedData: Record<string, any>[] = [];
  
  // Map API field keys to readable column names
  const fieldToColumnMap: Record<string, string> = {
    'd': 'Deal',
    'o': 'Order',
    'pid': 'Position',
    'l': 'Login',
    't': 'Time',
    'ac': 'Type',
    'e': 'Entry',
    's': 'Symbol',
    'v': 'Volume',
    'p': 'Price',
    'sl': 'S/L',
    'tp': 'T/P',
    'r': 'Reason',
    'c': 'Commission',
    'f': 'Fee',
    'pr': 'Profit',
    'de': 'Dealer',
    'co': 'Comment'
  };
  
  // Process data in batches using setTimeout to keep UI responsive
  const processBatch = (startIndex: number) => {
    const endIndex = Math.min(startIndex + batchSize, totalDeals);
    
    for (let i = startIndex; i < endIndex; i++) {
      const deal = deals[i];
      const formattedDeal: Record<string, any> = {};
      
      // Format each column according to the display requirements
      columns.forEach(column => {
        if (column === 'Name') {
          // Special handling for Name which combines first, last and middle names
          formattedDeal[column] = `${deal.firstn || ''} ${deal.lastn || ''} ${deal.middlen || ''}`.trim();
        } else {
          // Map API field to column name using our lookup table
          const apiField = Object.keys(fieldToColumnMap).find(
            key => fieldToColumnMap[key] === column
          );
          
          if (apiField && deal[apiField] !== undefined) {
            formattedDeal[column] = deal[apiField];
          } else {
            formattedDeal[column] = '';
          }
        }
      });
      
      processedData.push(formattedDeal);
    }
    
    // If there's more data to process, schedule next batch
    if (endIndex < totalDeals) {
      setTimeout(() => processBatch(endIndex), 0);
    } else {
      // All data processed, generate and download CSV
      const csvData = convertJsonToCsv(processedData, columns);
      downloadFile({
        data: csvData,
        fileName: `${fileName}.csv`,
        fileType: 'text/csv;charset=utf-8'
      });
    }
  };
  
  // Start processing the first batch
  processBatch(0);
}

export default downloadDealsToCSV;